/**
 * Router and global providers. BLUEPRINT section 18.
 *
 * A history router, not a hash router: Cloudflare Pages serves index.html for
 * every path through public/_redirects, so deep links work without a hash.
 *
 * Only the content panel changes between routes; the shell around it is
 * mounted once, which is what keeps the top nav, the toast layer and the
 * settings drawer alive across navigation.
 */

import { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from './AppShell';
import { ROUTES } from './routes';
import { ToastProvider, TooltipProvider } from '@/ui/system';
import { BotMatchPage, DeckTestPage } from '@/ui/pages/MatchSetupPage';
import { PlayPage } from '@/ui/pages/PlayPage';

// PLAY and the two match setup screens stay in the main chunk: they are the
// entry point and the thing a cold visitor almost always lands on. Everything
// else is split, which is what keeps the main bundle under the warning limit.
const CollectionPage = lazy(async () => ({
  default: (await import('@/ui/pages/CollectionPage')).CollectionPage,
}));
const StorePage = lazy(async () => ({
  default: (await import('@/ui/pages/StorePage')).StorePage,
}));
const ProfilePage = lazy(async () => ({
  default: (await import('@/ui/pages/ProfilePage')).ProfilePage,
}));
const HelpPage = lazy(async () => ({
  default: (await import('@/ui/pages/HelpPage')).HelpPage,
}));
const TestLabPage = lazy(async () => ({
  default: (await import('@/ui/pages/TestLabPage')).TestLabPage,
}));
const TutorialPage = lazy(async () => ({
  default: (await import('@/ui/pages/TutorialPage')).TutorialPage,
}));

/**
 * What a split route shows while its chunk arrives. Deliberately quiet: on a
 * warm cache it is on screen for a frame or two, and a spinner that flashes
 * reads as a glitch rather than as progress.
 */
function RouteFallback() {
  return (
    <div data-testid="route-loading" role="status" aria-live="polite" className="routeFallback">
      Loading
    </div>
  );
}

/** The route tree, without a router, so tests can wrap it in a MemoryRouter. */
export function AppRoutes() {
  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route path={ROUTES.play} element={<PlayPage />} />
        <Route path={ROUTES.playBot} element={<BotMatchPage />} />
        <Route
          path={ROUTES.playLab}
          element={
            <Suspense fallback={<RouteFallback />}>
              <TestLabPage />
            </Suspense>
          }
        />
        <Route path={ROUTES.playDeckTest} element={<DeckTestPage />} />
        <Route
          path={ROUTES.playTutorial}
          element={
            <Suspense fallback={<RouteFallback />}>
              <TutorialPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.collection}
          element={
            <Suspense fallback={<RouteFallback />}>
              <CollectionPage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.store}
          element={
            <Suspense fallback={<RouteFallback />}>
              <StorePage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.profile}
          element={
            <Suspense fallback={<RouteFallback />}>
              <ProfilePage />
            </Suspense>
          }
        />
        <Route
          path={ROUTES.help}
          element={
            <Suspense fallback={<RouteFallback />}>
              <HelpPage />
            </Suspense>
          }
        />
        <Route path="*" element={<Navigate to={ROUTES.play} replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <TooltipProvider>
          <AppRoutes />
        </TooltipProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}
